import { useTranslations } from 'next-intl'
import { Fragment, PropsWithChildren } from 'react'

import {
  AdditionalOption,
  OldAdditionalOption,
  TRoomDetail,
} from '@/entities/room'
import { cn } from '@/shared/lib'
import { Button, Drawer, Separator } from '@/shared/ui'

import { OptionList } from './option-list'
import { useOptionList } from '../../../lib/option'
import { SubHeading } from '../../common'

type Props = Pick<TRoomDetail, 'basicOptions' | 'missingOptions'> & {
  additionalOptions: (AdditionalOption | OldAdditionalOption)[]
}

export const OptionDrawerTrigger = ({
  children,
  basicOptions,
  additionalOptions,
  missingOptions,
}: PropsWithChildren<Props>) => {
  return (
    <Drawer.Root>
      <Drawer.Trigger asChild>{children}</Drawer.Trigger>
      <OptionDrawer
        basicOptions={basicOptions}
        additionalOptions={additionalOptions}
        missingOptions={missingOptions}
      />
    </Drawer.Root>
  )
}

export const OptionDrawer = ({
  basicOptions,
  additionalOptions,
  missingOptions,
}: Props) => {
  const t = useTranslations('room_detail')

  const optionList = useOptionList({
    basicOptions,
    additionalOptions,
    missingOptions,
  })

  return (
    <Drawer.Content className="flex max-h-[95dvh] flex-col gap-0 p-0">
      <Drawer.Header className="px-4 pb-5">
        <Drawer.Title>{t('option.modal.title')}</Drawer.Title>
        <Drawer.Description className="sr-only">
          {t('option.modal.title')}
        </Drawer.Description>
      </Drawer.Header>
      <div className="flex grow flex-col overflow-y-auto">
        {optionList.map(({ key, title, options }, index) => (
          <Fragment key={key}>
            <div className={cn('space-y-3 px-4')}>
              <SubHeading className="leading-6!">{title}</SubHeading>
              <OptionList
                options={options}
                disabled={key === 'missingOptions'}
                className="gap-x-10"
              />
            </div>

            {index !== optionList.length - 1 && (
              <Separator color="light-gray" size="medium" className="my-5" />
            )}
          </Fragment>
        ))}
      </div>

      <Drawer.Footer className="px-4 py-6">
        <Drawer.Close asChild>
          <Button size="large" color="secondary">
            {t('option.modal.button.close')}
          </Button>
        </Drawer.Close>
      </Drawer.Footer>
    </Drawer.Content>
  )
}
